import NotificationsIcon from '@mui/icons-material/Notifications';
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Stack,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  getUnreadCount,
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from './api';

export function NotificationBell(): JSX.Element {
  const { t } = useTranslation();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = Boolean(anchor);
  const queryClient = useQueryClient();

  const countQ = useQuery({
    queryKey: ['notif-unread'],
    queryFn: getUnreadCount,
    refetchInterval: 30_000,
  });

  const listQ = useQuery({
    queryKey: ['notif-recent'],
    queryFn: () => listNotifications({ page: 0, size: 10 }),
    enabled: open,
  });

  const markRead = useMutation({
    mutationFn: markNotificationRead,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notif-unread'] });
      void queryClient.invalidateQueries({ queryKey: ['notif-recent'] });
    },
  });

  const markAll = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notif-unread'] });
      void queryClient.invalidateQueries({ queryKey: ['notif-recent'] });
    },
  });

  return (
    <>
      <IconButton color="inherit" onClick={(e) => setAnchor(e.currentTarget)}
        aria-label={t('notifications.title')}>
        <Badge badgeContent={countQ.data ?? 0} color="error" max={99}>
          <NotificationsIcon />
        </Badge>
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 360, maxHeight: 480 } } }}
      >
        <Box sx={{ p: 1.5 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle1" fontWeight={600}>
              {t('notifications.title')}
            </Typography>
            <Button size="small" onClick={() => markAll.mutate()} disabled={(countQ.data ?? 0) === 0}>
              {t('notifications.bell.markAllRead')}
            </Button>
          </Stack>
        </Box>
        <Divider />
        {listQ.isLoading && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <CircularProgress size={22} />
          </Box>
        )}
        {!listQ.isLoading && (listQ.data?.content.length ?? 0) === 0 && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {t('notifications.bell.empty')}
            </Typography>
          </Box>
        )}
        <List dense disablePadding>
          {listQ.data?.content.map((n) => (
            <ListItemButton
              key={n.id}
              onClick={() => {
                if (!n.readAt) markRead.mutate(n.id);
              }}
              sx={{ bgcolor: n.readAt ? 'transparent' : 'action.hover' }}
            >
              <ListItemText
                primary={n.title}
                secondary={
                  <>
                    <Typography variant="caption" color="text.secondary">
                      {t(`notifications.categories.${n.category}`)} · {new Date(n.createdAt).toLocaleString()}
                    </Typography>
                    <Typography variant="body2">{n.body}</Typography>
                  </>
                }
                secondaryTypographyProps={{ component: 'div' }}
              />
            </ListItemButton>
          ))}
        </List>
        <Divider />
        <Box sx={{ p: 1, textAlign: 'center' }}>
          <Button
            component={RouterLink}
            to="/notifications/preferences"
            size="small"
            onClick={() => setAnchor(null)}
          >
            {t('nav.notificationPrefs')}
          </Button>
        </Box>
      </Popover>
    </>
  );
}
